import { useState, useContext, useEffect } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Plus,
  Search,
  Trash2,
  Pencil,
  Eye,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  IndianRupee,
  StickyNote,
  CheckCircle2,
  XCircle,
  RefreshCcw,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

// yyyy-MM-dd -> dd/MM/yyyy (for display + API accepts both)
const isoToDdMmYyyy = (iso) => {
  if (!iso) return "";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
};

// dd/MM/yyyy -> yyyy-MM-dd (for <input type="date" />)
const ddMmYyyyToIso = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const m = String(ddmmyyyy).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const BataHalfDayAmountEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade, lightColor, lightShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const lightButtonClass = `bg-${lightColor}-${lightShade}`;

  const [errMsg, setErrMsg] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const finYearId = localStorage.getItem("finYearId") || "";

  // collapse
  const [showForm, setShowForm] = useState(true);

  // form fields (we keep dd/MM/yyyy in state, like ASP)
  const [date, setDate] = useState(""); // dd/MM/yyyy
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // search
  const [search, setSearch] = useState("");

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const resetMsgs = () => {
    setErrMsg("");
    setOkMsg("");
  };

  const resetForm = () => {
    setDate("");
    setAmount("");
    setRemarks("");
    setIsActive(false);
    setEditingId(null);
  };

  const fetchList = async (goToPage = page) => {
    resetMsgs();

    if (!String(finYearId).trim()) {
      setErrMsg("Please select Financial Year.");
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bata-halfday`, {
        params: {
          finYearId: Number(finYearId),
          page: goToPage,
          pageSize: limit,
          search: search.trim(),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);

        setSelectedIds([]);
        setSelectAll(false);
      } else {
        const msg = res?.data?.message || "Failed to load records.";
        setErrMsg(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleAddNew = () => {
    resetForm();
    setShowForm(true);
    toast.info("Form ready for new entry");
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    resetMsgs();

    if (!String(finYearId).trim()) {
      setErrMsg("Please select Financial Year.");
      return;
    }

    if (!date.trim() || !amount.trim()) {
      setErrMsg("Date and Amount are required.");
      toast.warn("Date and Amount are required.");
      return;
    }

    if (Number.isNaN(Number(amount))) {
      setErrMsg("Amount must be numeric.");
      toast.error("Amount must be numeric.");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        date: date.trim(), // dd/MM/yyyy (API accepts)
        amount: amount.trim(),
        remarks: remarks || "",
        isActive: !!isActive,
        userId: localStorage.getItem("UserLoginId") || "",
      };

      let res;
      if (editingId) {
        res = await axios.put(`${BASE_URL}/api/bata-halfday/${editingId}`, payload, {
          params: { finYearId: Number(finYearId) },
        });
      } else {
        res = await axios.post(`${BASE_URL}/api/bata-halfday`, payload, {
          params: { finYearId: Number(finYearId) },
        });
      }

      if (res?.data?.success) {
        const msg =
          res?.data?.message || (editingId ? "Successfully Updated" : "Successfully Inserted");
        setOkMsg(msg);
        toast.success(msg);

        resetForm();
        setPage(1);
        await fetchList(1);
      } else {
        const msg = res?.data?.message || "Operation failed.";
        setErrMsg(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    resetMsgs();

    if (!String(finYearId).trim()) {
      setErrMsg("Please select Financial Year.");
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/bata-halfday/${id}`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && res?.data?.row) {
        const r = res.data.row;

        setEditingId(r.id);
        setDate(r.fld_date || ""); // dd/MM/yyyy from API
        setAmount(r.fld_amount?.toString?.() || "");
        setRemarks(r.fld_remarks || "");
        setIsActive(String(r.fld_isactive) === "1" || r.fld_isactive === 1);

        setShowForm(true);
        toast.info("Loaded for editing");
      } else {
        const msg = res?.data?.message || "Record not found.";
        setErrMsg(msg);
        toast.warn(msg);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (r) => {
    toast.info(`Date: ${r.fld_date} | Amount: ${r.fld_amount} | ${r.fld_approved}`);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.id));
    setSelectAll(true);
  };

  const handleDeleteSelected = async () => {
    resetMsgs();

    if (!String(finYearId).trim()) {
      setErrMsg("Please select Financial Year.");
      toast.warn("Please select Financial Year.");
      return;
    }

    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record to delete!");
      return;
    }

    const ok = window.confirm(`Are you sure to delete ${selectedIds.length} record(s)?`);
    if (!ok) return;

    try {
      setLoading(true);

      await Promise.all(
        selectedIds.map((id) =>
          axios.delete(`${BASE_URL}/api/bata-halfday/${id}`, {
            params: { finYearId: Number(finYearId) },
          })
        )
      );

      setOkMsg("Removed successfully");
      toast.success(`Deleted ${selectedIds.length} record(s) successfully!`);

      setSelectedIds([]);
      setSelectAll(false);

      const nextPage = page > 1 && rows.length === selectedIds.length ? page - 1 : page;
      setPage(nextPage);
      await fetchList(nextPage);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrMsg(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const showingTo = Math.min(page * limit, total);

  const inputClass = `w-full px-3 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`;

  const primaryBtn = `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass}`;
  const accentBtn = `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${lightButtonClass}`;
  const softBtn = `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold border transition-all duration-200 ${
    isDark ? "border-gray-600 text-white hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"
  }`;

  return (
    <PayRollLayoutWrapper>
      {/* ✅ Toastify OUTSIDE container */}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATA HALF DAY AMOUNT ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black`
          }`}
        >
          {/* Alerts */}
          {(errMsg || okMsg) && (
            <div className="mb-6 space-y-3">
              {errMsg ? (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800">
                  <XCircle className="w-5 h-5 mt-0.5" />
                  <div>
                    <div className="font-semibold">Error</div>
                    <div className="text-sm">{errMsg}</div>
                  </div>
                </div>
              ) : null}

              {okMsg ? (
                <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-50 border border-green-200 text-green-800">
                  <CheckCircle2 className="w-5 h-5 mt-0.5" />
                  <div>
                    <div className="font-semibold">Success</div>
                    <div className="text-sm">{okMsg}</div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <IndianRupee className={isDark ? "text-white" : "text-gray-700"} />
              </div>
              <div>
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Bata Half Day Amount
                </h1>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Modern UI + Date Picker + Pagination
                </div>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {total} Records
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button onClick={() => setShowForm((v) => !v)} className={softBtn}>
                {showForm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>

              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => fetchList(page)} className={softBtn}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Search */}
          <form
            onSubmit={handleSearch}
            className={`rounded-2xl p-5 mb-6 border shadow-sm ${
              isDark ? "bg-gray-800/70 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex flex-col md:flex-row items-stretch md:items-end gap-4">
              <div className="flex-1">
                <label className={labelClass}>Search (date / amount / remarks)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Type to search..."
                    className={`pl-10 ${inputClass}`}
                  />
                </div>
              </div>

              <button type="submit" className={primaryBtn}>
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
          </form>

          {/* Collapsible Form */}
          {showForm && (
            <form
              onSubmit={handleSave}
              className={`rounded-2xl p-6 mb-6 border shadow-lg ${
                isDark
                  ? "bg-gradient-to-br from-gray-800/80 to-gray-900/70 border-gray-700"
                  : "bg-white border-gray-200"
              }`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                    <StickyNote className={isDark ? "text-white" : "text-gray-700"} />
                  </div>
                  <div>
                    <div className="text-lg font-semibold">{editingId ? "Edit Entry" : "New Entry"}</div>
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      Date + Amount required
                    </div>
                  </div>
                </div>

                {editingId ? (
                  <div className={`${isDark ? "bg-yellow-900/50 text-yellow-100" : "bg-yellow-100 text-yellow-800"} px-4 py-2 rounded-xl text-sm font-medium`}>
                    Editing ID: <span className="font-bold">{editingId}</span>
                  </div>
                ) : null}
              </div>

              {/* ✅ aligned grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* ✅ Date Picker */}
                <div>
                  <label className={labelClass}>
                    Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CalendarDays className="w-4 h-4 text-gray-400" />
                    </div>

                    <input
                      type="date"
                      value={ddMmYyyyToIso(date)}
                      onChange={(e) => setDate(isoToDdMmYyyy(e.target.value))}
                      className={`pl-10 ${inputClass}`}
                    />
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    Saved & displayed as dd/MM/yyyy (like ASP)
                  </p>
                </div>

                <div>
                  <label className={labelClass}>
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IndianRupee className="w-4 h-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className={`pl-10 ${inputClass}`}
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Remarks</label>
                  <input
                    type="text"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Remarks..."
                    className={inputClass}
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className={labelClass}>IsActive (Approved)</label>
                  <div className={`mt-2 flex items-center justify-between gap-4 p-3 rounded-xl border ${isDark ? "border-gray-700 bg-white/5" : "border-gray-200 bg-gray-50"}`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        className="w-5 h-5"
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>
                        {isActive ? "Approved" : "Approve"}
                      </span>
                    </div>

                    <div className={`text-xs px-3 py-1 rounded-full font-semibold ${
                      isActive
                        ? isDark ? "bg-emerald-900/50 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                        : isDark ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-700"
                    }`}>
                      {isActive ? "ACTIVE" : "INACTIVE"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-end gap-3 mt-7">
                <button type="button" onClick={resetForm} className={softBtn}>
                  <RefreshCcw className="w-4 h-4" />
                  Clear
                </button>

                <button type="submit" disabled={loading} className={`${accentBtn} ${loading ? "opacity-70 cursor-not-allowed" : ""}`}>
                  <Plus className="w-4 h-4" />
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={`rounded-2xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold">S.No</th>
                    <th className="px-4 py-4 text-center font-semibold">Date</th>
                    <th className="px-4 py-4 text-center font-semibold">Amount</th>
                    <th className="px-4 py-4 text-center font-semibold">Remarks</th>
                    <th className="px-4 py-4 text-center font-semibold">Approve</th>
                    <th className="px-4 py-4 text-center font-semibold">Actions</th>
                    <th className="px-4 py-4 text-center font-semibold">
                      <div className="flex items-center justify-center">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} className="ml-2" />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: limit }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-4 py-3">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={r.id} className={`transition-colors duration-200 ${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * limit + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_date}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_amount}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_remarks}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center`}>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            String(r.fld_isactive) === "1" || r.fld_isactive === 1
                              ? isDark ? "bg-emerald-900/50 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                              : isDark ? "bg-gray-700 text-gray-200" : "bg-gray-200 text-gray-700"
                          }`}>
                            {r.fld_approved}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(r.id)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-xl hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <Pencil className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleView(r)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white text-xs font-semibold rounded-xl hover:bg-emerald-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg"
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </button>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(r.id)}
                            onChange={() => toggleSelect(r.id)}
                            className="w-4 h-4"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination + Delete */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
              Showing <span className="font-semibold">{showingFrom}</span> to{" "}
              <span className="font-semibold">{showingTo}</span> of{" "}
              <span className="font-semibold">{total}</span> results
            </div>

            <div className="flex items-center gap-4">
              {!loading && totalPages > 1 && (
                <div className="inline-flex rounded-xl shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 font-sans">
                  <button
                    onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-medium ${
                      page === 1
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-medium transition ${
                        page === i + 1
                          ? `${lightButtonClass} text-white`
                          : `hover:bg-gray-100 text-gray-700 dark:hover:bg-gray-800 dark:text-gray-200`
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-medium ${
                      page === totalPages
                        ? "text-gray-400 cursor-not-allowed"
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button
                onClick={handleDeleteSelected}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : `${buttonClass} ${hoverClass}`
                }`}
                disabled={selectedIds.length === 0}
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default BataHalfDayAmountEntry;
